import { useEffect, useRef } from 'react';
import { REACT_APP_WS_URL } from '../config/env';
import { createWsBackoff, onNetworkWake } from './wsReconnect';

/**
 * useNotificationSocket — a global, session-long subscription to the
 * /ws/inbox channel that fires `onSignal` whenever the server publishes a
 * `notification.new` nudge (notificationService.createNotification →
 * publishToUser). The client then refetches its summary + list, so the bell
 * updates in real time without polling. Mirrors useInboxSocket's reconnect
 * pattern; mounted once inside NotificationProvider.
 */
function getWsBase(): string {
  if (REACT_APP_WS_URL) return REACT_APP_WS_URL;
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}`;
}

export function useNotificationSocket(onSignal: () => void): void {
  const cbRef = useRef(onSignal);
  cbRef.current = onSignal;

  useEffect(() => {
    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let unmounted = false;
    // PHONE-A4 (V-12) — the same jittered backoff + wake policy as
    // useInboxSocket, from the one shared module. This hook is mounted once per
    // session for the whole app, so its flat 5-second retry was the SECOND
    // socket hammering /ws/inbox from every logged-out tab: two connections per
    // tab, both retrying forever. See hooks/wsReconnect.ts.
    const backoff = createWsBackoff();

    function schedule(delayMs: number) {
      if (unmounted) return;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(connect, delayMs);
    }

    function connect() {
      if (unmounted) return;
      try {
        ws = new WebSocket(`${getWsBase()}/ws/inbox`);
      } catch {
        schedule(backoff.nextDelay());
        return;
      }

      ws.onopen = () => { backoff.noteOpen(); };

      ws.onmessage = (e) => {
        try {
          const event = JSON.parse(e.data);
          if (event?.type === 'notification.new') cbRef.current();
        } catch { /* ignore non-JSON / unrelated inbox events */ }
      };

      ws.onclose = () => {
        ws = null;
        backoff.noteClosed();
        if (!unmounted) schedule(backoff.nextDelay());
      };

      ws.onerror = () => {
        try { ws?.close(); } catch { /* ignore */ }
      };
    }

    const stopWake = onNetworkWake(() => {
      if (unmounted) return;
      const state = ws?.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) return;
      backoff.reset();
      schedule(0);
    });

    schedule(500);

    return () => {
      unmounted = true;
      stopWake();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) {
        try {
          if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) ws.close();
        } catch { /* ignore */ }
        ws = null;
      }
    };
  }, []);
}
